import { Component } from 'react';

/**
 * Catches render-time errors so a single bad component cannot white-screen the
 * whole app with no way out — the worst case on a phone with a flaky
 * connection, where reinstalling is the only obvious remedy and that costs the
 * user their device token.
 *
 * Strings are passed in from the caller (which has i18n context) because a
 * class component sits outside hooks, and this must keep working even if the
 * failure is in the i18n layer itself.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Unhandled render error:', error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    const { title, description, retry, reload } = this.props.strings || {};
    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-background p-6"
        data-testid="error-boundary"
      >
        <div className="max-w-md w-full space-y-4 text-center">
          <h1 className="font-heading text-lg text-foreground">
            {title || 'Something went wrong'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {description || 'The app hit an unexpected error. Your data is safe on the server.'}
          </p>
          <pre className="text-xs text-left text-muted-foreground bg-muted rounded-md p-3 overflow-x-auto">
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              className="px-4 py-2 text-sm rounded-md border border-border hover:bg-muted"
              onClick={() => this.setState({ error: null })}
            >
              {retry || 'Try again'}
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => window.location.reload()}
            >
              {reload || 'Reload'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
